import {FieldValue} from "@firebase/firestore";

export type VideoProvider = "youtube" | "vimeo" | "braiv" | "external";
export type Visibility = "public" | "private";
export type LibraryVisibility = "visible" | "hidden";
export type UserVideoOrigin = "desktop_capture";
export type YoutubePrivacy = "public" | "private" | "unlisted" | "scheduled";
export type VideoProcessingStatus =
    | "idle"
    | "importing"
    | "import-failed"
    | "uploading"
    | "upload-failed"
    | "upload-cancelled"
    | "upload-complete"
    | "splitting-audio"
    | "transcoding"
    | "translating"
    | "synthesizing-audio"
    | "complete";

export interface BaseVideo {
    id: string;
    version: number;
    creator: string;
    // Team workspaces (MVP): when set, the video belongs to a team workspace.
    // `creator` holds the workspace's immutable storageAnchorUid (so storage and
    // pipeline are unchanged); `createdByUid` records the uploading member.
    // Personal videos leave both unset.
    workspaceId?: string;
    createdByUid?: string;
    processingStatus: VideoProcessingStatus;
    deleted: boolean;
    dateCreated: FieldValue | Date;
    lastUpdated: FieldValue | Date;
}

export interface UserVideo extends BaseVideo {
    url: string;
    urlMulti?: Record<string, string>;
    provider: VideoProvider;
    origin?: UserVideoOrigin;
    libraryVisibility?: LibraryVisibility;
    /** Stable Capture package identity for structured desktop imports. */
    desktopProjectId?: string;
    /** Currently promoted desktop import revision; independent of UserVideo.version. */
    desktopImportRevision?: number;
    activeDesktopImportRevisionId?: string;
    activeDesktopImportSessionId?: string;
    /** Optional playback-facing summaries when rendition records are denormalized. */
    desktopRenditions?: Record<string, DesktopVisualRendition>;
    title?: string;
    description?: string;
    /** Transcript-intelligence TLDR (auto on import/processing). */
    shortSummary?: string;
    posterUrl?: string;
    duration?: number;
    /**
     * Client-probed duration captured at upload, before transcode reports the
     * authoritative value. Lets billable work be priced at the final Continue.
     */
    durationEstimateSeconds?: number;
    /**
     * Which file the project player uses after Captions Only.
     * The processing source stays the original file either way.
     */
    playbackSource?: "original" | "captioned";
    /** Project URL captured before the first captioned file replaces playback. */
    originalPlaybackUrl?: string;
    captionedPlayback?: {
        url: string;
        storagePath: string;
        framing: "original" | "vertical";
        outputId: string;
        userShortsId?: string;
    };
    height?: number;
    width?: number;
    aspectRatio?: number;
    /** Daily materialized Pro Hosting analytics for library cards. */
    proHostingViewCount?: number;
    proHostingViewCountUpdatedAt?: FieldValue | Date;
    size?: number;
    visibility?: Visibility;
    youtubePrivacy?: YoutubePrivacy;
    scheduledStartTime?: string;
    branding?: BrandingSettings;
    languageOrder?: string[];
    customWords?: string[];
    defaultLanguageCode?: string;
    defaultLanguageLabel?: string;
    numberOfSpeakers?: number;
    skipVocalSeparation?: boolean;
    /** When true, ElevenLabs Scribe uses no_verbatim to drop fillers like um/uh. */
    noVerbatim?: boolean;
    tags?: string[];
    seenProcessNotificationModal?: boolean;
    flagSelections?: { [languageCode: string]: string };
    languages?: {
        [languageCode: string]: {
            title: string;
            description: string;
            thumbnail?: string;
            /** Per-type description bodies for this locale (mirrors root map). */
            generatedDescriptions?: Partial<Record<
                "youtube-description" | "social-post" | "how-to-guide" | "short-summary" |
                "blog-post" | "video-summary" | "imported",
                string
            >>;
            activeDescriptionType?:
                "youtube-description" | "social-post" | "how-to-guide" | "short-summary" |
                "blog-post" | "video-summary" | "imported";
        };
    };
    languageQuota?: {
        addedLanguageCodes?: string[];
        lastUpdated?: FieldValue | Date;
    };
    generatedTitles?: {
        [titleType: string]: string;
    };
    generatedDescriptions?: Partial<Record<
        "youtube-description" | "social-post" | "how-to-guide" | "short-summary" | "blog-post" | "video-summary" | "imported",
        string
    >>;
    activeDescriptionType?:
        "youtube-description" | "social-post" | "how-to-guide" | "short-summary" | "blog-post" | "video-summary" | "imported";
    generatedYoutubeTags?: string[];
    primaryGenTitle?: string;
    isYouTubeVideo?: boolean;
    youtubeVideoId?: string;
    youtubeVideoIds?: string[];
    youtubeVideoData?: YouTubeVideoData;
    exportStatus?: 'exported' | 'not_exported' | 'import_available';
    // Linked import fields - set when video is imported from a connected YouTube channel
    isLinkedImport?: boolean;
    linkedIntegrationId?: string;
    linkedChannelId?: string;
    thumbnailGenerations?: string[];
    thumbnailGenerationsMap?: {
        [thumbnailUrl: string]: {
            prompt: string;
            userPrompt?: string;
            editPrompt?: string;
            promptSource?: "user" | "zero-prompt" | "studio";
            generatedBy?: string;
            generationType?: string;
            pipelineMode?: "thumbnail-v2" | "thumbnail-precision" | "v1";
            referenceSchemaVersion?: 1 | 2;
            parentThumbnailUrl?: string;
            contentReferenceData?: Array<{
                imageUrl: string;
            }>;
            textOverlayData?: Array<{
                text: string;
                position: string; // "top" or "bottom"
            }>;
            imageOverlayData?: Array<{
                imageUrl: string;
                name?: string;
            }>;
            characterReferenceData?: Array<{
                imageUrl?: string;
                imageUrls?: string[];
                characterId?: string;
                referenceImageIds?: string[];
                name?: string;
                position?: string;
                action?: string;
            }>;
            styleReferenceData?: Array<{
                imageUrl: string;
                influence?: number; // v1 aesthetic reference only
                analysis?: unknown; // v2 recreate analysis snapshot
                analysisVersion?: number;
            }> | {
                imageUrl: string;
                influence?: number; // v1 aesthetic reference only
                analysis?: unknown; // v2 recreate analysis snapshot
                analysisVersion?: number;
            };
            brandReferenceData?: Array<{
                imageUrl: string;
                influence: number;
            }> | {
                imageUrl: string;
                influence: number;
            };
        };
    };
    // Credit tracking
    totalCreditsSpent?: number;
    // Marketing rebate tracking
    marketingRebateApplied?: boolean;
    marketingRebateAmount?: number;
    mediaType?: "video" | "audio";
    pipelineLane?: "video" | "audio";
    llmIabCategories?: string | string[];
    llmIabCategoryLabel?: string;
    /** Denormalized from the video owner's active subscription (personal or workspace billing). */
    creatorHasActiveSubscription?: boolean;
    thumbnailSuggestedPrompts?: {
        basic?: string;
        professional?: string;
        creative?: string;
    };
    thumbnailSuggestedPromptsGeneratedAt?: FieldValue | Date;
    pendingShortsConfig?: PendingShortsConfig;
    /**
     * Playback-facing dub metadata attached by the public video data loader.
     * This is optional so legacy payloads continue to treat existing tracks as full.
     */
    dubTracks?: DubTrack[];
}

export type DesktopVisualRenditionStatus = "pending" | "ready" | "failed";
export type DesktopPackagingProvider = "shaka" | "mux";

export interface DesktopVisualRendition {
    id?: string;
    userVideoId: string;
    userVideoVersion: number;
    sourceVideoId?: string;
    creator: string;
    workspaceId?: string;
    createdByUid?: string;
    languageCode: string;
    sourceAssetId?: string;
    desktopImportSessionId: string;
    activationStatus: "staged" | "active" | "stale" | "superseded";
    status: DesktopVisualRenditionStatus;
    provider?: DesktopPackagingProvider;
    packagedUrl?: string;
    manifestUrl?: string;
    progressiveUrl?: string;
    providerAssetId?: string;
    muxPlaybackId?: string;
    muxTrackIds?: string[];
    posterUrl?: string;
    width?: number;
    height?: number;
    durationSeconds?: number;
    errorMessage?: string;
    dateCreated?: FieldValue | Date;
    lastUpdated?: FieldValue | Date;
    activatedAt?: FieldValue | Date;
}

export type PendingShortsConfigStatus = "pending" | "firing" | "done" | "failed";

export interface PendingShortsConfig {
    status: PendingShortsConfigStatus;
    config?: Record<string, unknown>;
    requestedAt?: FieldValue | Date;
    firedAt?: FieldValue | Date;
    completedAt?: FieldValue | Date;
    attempts?: number;
    shortsRequestId?: string;
    lastError?: string;
}

export interface VideoMetadata {
    url?: string;
    title?: string;
    description?: string;
    posterUrl?: string;
    height?: number;
    width?: number;
    aspectRatio?: number;
    error?: string;
}

export interface YouTubeVideoData {
    id: string;
    title: string;
    description: string;
    thumbnailUrl: string;
    /** Raw best-CDN thumbnail URL from the latest sync, separate from the Braiv-preferred display thumbnailUrl. Server-written. */
    youtubeThumbnailUrl?: string;
    publishedAt: string;
    duration: string;
    viewCount: string;
    likeCount?: string;
    channelId: string;
    channelTitle: string;
    type?: 'video' | 'short' | 'shorts' | 'long';
    language?: string;
    localizations?: {
        [languageCode: string]: { title: string; description: string };
    };
    titleScore?: number | string;
    descriptionScore?: number | string;
    scores?: {
        title?: number | string;
        description?: number | string;
        thumbnail?: number | string;
    };
    titleScoreFeedback?: string;
    titleScoreImprovements?: string[] | string;
    /** sha256 fingerprint of the title this score was computed from. Server-written debug field. */
    titleScoreSourceHash?: string;
    descriptionScoreFeedback?: string;
    descriptionScoreImprovements?: string[] | string;
    /** sha256 fingerprint of the description this score was computed from. Server-written debug field. */
    descriptionScoreSourceHash?: string;
    thumbnailScore?: number | string;
    thumbnailScoreFeedback?: {
        topicMatch?: ThumbnailPillarFeedback;
        clarity?: ThumbnailPillarFeedback;
        standout?: ThumbnailPillarFeedback;
        curiosity?: ThumbnailPillarFeedback;
        emotion?: ThumbnailPillarFeedback;
    };
    thumbnailScoreImageUrl?: string;
    /** Compound (imageUrl + etag) fingerprint the thumbnail score was computed from. Server-written debug field. */
    thumbnailScoreSourceKey?: string;
    draft?: {
        title?: string;
        description?: string;
        /** Favourite thumbnail waiting to be pushed to YouTube via Sync. */
        thumbnailUrl?: string;
        localizations?: {
            [languageCode: string]: { title?: string; description?: string };
        };
        updatedAt?: unknown;
        updatedBy?: string;
        status?: string;
    };
    docId?: string;
    captions?: {
        defaultLanguage?: string;
        tracks?: Array<{
            language?: string;
            languageCode?: string;
            code?: string;
            srtUrl?: string;
            srtURL?: string;
        }> | Record<string, unknown>;
    };
    privacyStatus?: YoutubePrivacy | string;
    /** Durable transcript intelligence mirrored from Braiv projects. Server-written. */
    intelligence?: {
        version?: number;
        origin?: 'youtube_captions' | 'braiv_transcription';
        sourceHash?: string;
        shortSummary?: string;
        llmVideoFormats?: string[];
        llmIabCategories?: string[];
        sourceLanguageCode?: string;
    };
    /** Back-pointers to the Braiv records derived from this video. Server-written. */
    braiv?: {
        userVideoId?: string;
        userThumbnailId?: string;
    };
}

export interface ThumbnailPillarFeedback {
    score: number;
    strengths: string;
    critique: string;
    actionableFix: string;
}

export interface UploadVideo extends BaseVideo {
    id: string;
    uploadPath: string;
    duration?: number;
}

export interface UserVideoTranscript {
    id: string;
    json?: object;
    text?: string;
    srt?: string;
    vtt?: string;
}

export type RoundedCornerSizeType = "none" | "small" | "medium" | "large";
export type PlayButtonShape = "circle" | "rounded" | "square";
export type LanguageMenuLocation = "top" | "settings";

export interface BrandingSettings {
    brandColor: string;
    accentColor: string;
    menuBackgroundColor: string;
    menuTextColor: string;
    logoUrl: string;
    displayLogo: boolean;
    displayVideoTitle: boolean;
    centrePlayButton: boolean;
    centrePlayButtonShape: PlayButtonShape;
    playbackControls: boolean;
    roundedCorners?: boolean;
    roundedCornersSize: RoundedCornerSizeType;
    defaultQuality: string;
    autoPlay: boolean;
    speedControl: boolean;
    autoMute: boolean;
    autoCaptions: boolean;
    loopVideo: boolean;
    minimalEmbedView: boolean;
    captionTextColor: string;
    captionHighlightColor: string;
    publicPageEnabled: boolean;
    publicPagePasswordEnabled?: boolean;
    publicPagePassword?: string;
    publicPageBrandingActive: boolean;
    publicPageBackgroundColor: string;
    publicPageButtonColor: string;
    publicPageButtonTextColor: string;
    publicPageLogoUrl: string;
    displayShareButton: boolean;
    displayShortsButton?: boolean;
    displayOptimizeButton?: boolean;
    displayDownloadButton?: boolean;
    displayCtaButton: boolean;
    ctaButtonUrl: string;
    ctaButtonLabel: string;
    displayCustomContent: boolean;
    displayFlags: boolean;
    languageMenuLocation: LanguageMenuLocation;
    languageAutoSwitch: boolean;
    schemaMarkupEnabled?: boolean;
}

export interface BrandingTemplate extends BrandingSettings {
    id: string | undefined;
    displayName: string;
    ownerUid: string;
    workspaceId?: string;
    createdByUid?: string;
}

export interface BrandVoiceSocialLinks {
    youtube?: string;
    instagram?: string;
    tiktok?: string;
    linkedin?: string;
    twitter?: string;
    facebook?: string;
    podcast?: string;
    website?: string;
    custom?: Array<{ label: string; url: string }>;
}

export interface BrandVoiceWebsiteAssets {
    homepageUrl?: string;
    palette?: Array<{ name: string; hex: string }>;
    logoUrls?: string[];
    faviconUrl?: string;
    lastScannedAt?: Date;
    scanStatus?: "idle" | "scanning" | "complete" | "error";
}

export interface BrandVoiceProfile {
    samplePosts: string[];
    analyzedTone?: string;
    analyzedStyleGuide?: string;
    analyzedKeywords?: string[];
    voiceSummary?: string;
}

export interface BrandVoiceThumbnailTypography {
    casing: string;
    weightFeel: string;
    strokeShadow: string;
    badgeOrBanner: string;
    fontFeel: string;
}

export interface BrandVoiceThumbnailPalette {
    dominant: string[];
    accents: string[];
    contrastMood: string;
}

export interface BrandVoiceThumbnailOverlayChrome {
    edgeFades: string;
    vignettes: string;
    insetsAndStrokes: string;
    colourWashes: string;
    required: string[];
    optional: string[];
}

export interface BrandVoiceThumbnailStyle {
    referenceImageUrls: string[];
    styleNotes?: string;
    analyzedStyleSummary?: string;
    layoutGrammar?: string;
    typography?: BrandVoiceThumbnailTypography;
    palette?: BrandVoiceThumbnailPalette;
    overlayChrome?: BrandVoiceThumbnailOverlayChrome;
    mood?: string;
    doNotCopy?: string[];
    analyzedAt?: Date;
}

export interface BrandVoice {
    id?: string;
    ownerUid: string;
    workspaceId?: string;
    createdByUid?: string;
    name: string;
    linkedChannelId?: string;
    linkedChannelTitle?: string;
    linkedChannelThumbnail?: string;
    socialLinks: BrandVoiceSocialLinks;
    websiteAssets: BrandVoiceWebsiteAssets;
    voiceProfile: BrandVoiceProfile;
    thumbnailStyle: BrandVoiceThumbnailStyle;
    isDefault: boolean;
    dateCreated: Date;
    lastUpdated: Date;
}

/** Smartling-aligned branding glossary (one default per personal user or workspace). */
export interface BrandGlossary {
    id?: string;
    ownerUid: string;
    workspaceId?: string;
    createdByUid?: string;
    name: string;
    sourceLocale: string;
    locales: string[];
    isDefault: boolean;
    dateCreated: Date;
    lastUpdated: Date;
}

export interface BrandGlossaryEntry {
    id?: string;
    uid?: string;
    terms: Record<string, string>;
    definition?: string;
    dnt: boolean;
    partOfSpeech?: string;
    termVariations?: string[];
    notes?: string;
    caseSensitive: boolean;
    dateCreated?: Date;
    lastUpdated?: Date;
}

export interface UserVideoTranscript {
    id: string;
    json?: object;
    text?: string;
    srt?: string;
    vtt?: string;
}

export interface SubtitleTrackInfo {
    vttUrl: string;
    label: string;
    lang: string;
    regionLang: string;
}

export interface SourceVideo {
    id: string;
    userVideoId: string;
    creator: string;
    processingStatus: VideoProcessingStatus;
    url: string;
    audioOnlyUrl?: string;
    muxAudioUrl?: string;
    storageFullPath: string;
    storageBucket: string;
    storagePath: string;
    videoOnlyUrl?: string;
    version: number;
    fileName: string;
    originalFileName: string;
    size: number;
    muxId?: string;
    muxUrl?: string;
    dateCreated: FieldValue | Date;
    lastUpdated: FieldValue | Date;
    deleted: boolean;
}

export type DubDeliveryMode = "preview" | "full";

export type DubCompletionStatus =
    | "preview_pending"
    | "preview_ready"
    | "full_queued"
    | "full_processing"
    | "full_ready"
    | "awaiting_credits"
    | "completion_failed";

export interface DubCoverage {
    mode: DubDeliveryMode;
    previewTargetSeconds?: number;
    previewEndSegmentIndex?: number;
    previewEndSeconds?: number;
    sourceDurationSeconds?: number;
    totalSegmentCount?: number;
}

/**
 * Minimal metadata needed by player surfaces. Backends may expose this from
 * AudioTracks or a dedicated DubTracks collection.
 */
export interface DubTrack {
    id?: string;
    languageCode: string;
    label?: string;
    url?: string;
    status?: string;
    defaultTrack?: boolean;
    deliveryMode?: DubDeliveryMode;
    completionStatus?: DubCompletionStatus;
    /**
     * Credits still required to finish a preview dub (delta after preview charge).
     * Written when completionStatus becomes awaiting_credits.
     */
    completionCreditsRequired?: number;
    coverage?: DubCoverage;
    /** Present when the track was produced from a DubbingRequest. */
    dubbingRequestId?: string;
    /** Structured desktop imports expose only playback-role tracks to the player. */
    trackRole?: "vocal" | "background" | "playback";
    desktopManaged?: boolean;
    desktopImportActive?: boolean;
    desktopImportRevision?: number;
    desktopImportRevisionId?: string;
    desktopImportSessionId?: string;
    desktopTimelineHash?: string;
}

export interface AudioTrack extends DubTrack {
    id?: string;
    userVideoId: string;
    userVideoVersion: number;
    url: string;
    languageCode: string;
    label: string;
    size: number;
    status?: string;
    fileType: string;
    fileName: string;
    originalFileName: string;
    storageFullPath: string;
    storageBucket: string;
    storagePath: string;
    dateCreated: FieldValue | Date;
    lastUpdated: FieldValue | Date;
    defaultTrack?: boolean;
}

export type SubtitleFormat = "srt" | "vtt" | "json";

export interface SubtitleTrack {
    id?: string;
    version: number;
    userVideoId: string;
    format: SubtitleFormat;
    url: string;
    languageCode: string;
    label: string;
    size: number;
    fileType: string;
    fileName: string;
    originalFileName: string;
    storageFullPath: string;
    storageBucket: string;
    storagePath: string;
    standardVttUrl?: string;
    standardVttStoragePath?: string;
    karaokeVttUrl?: string;
    karaokeVttStoragePath?: string;
    dateCreated: FieldValue | Date;
    lastUpdated: FieldValue | Date;
}

export interface UserPermissions {
    uid?: string;
    admin: boolean;
    betaProgram: boolean;
    dubbing: boolean;
    upload: boolean;
    dateCreated: Date;
    lastUpdated: Date;
}

export type UserPermissionFlags = Pick<UserPermissions, 'admin' | 'betaProgram' | 'dubbing' | 'upload'>;

export const DEFAULT_PERMISSION_FLAGS: UserPermissionFlags = {
    admin: false,
    betaProgram: true,
    dubbing: true,
    upload: true,
};

export interface AdminAction {
    id: string;
    action: string;
    performedBy: string;
    performedByEmail?: string;
    targetUid: string;
    changes: Record<string, any>;
    timestamp: Date;
}

export interface BillingAccount {
    id: string;
    displayName: string;
    balance: number;
    dateCreated: Date | FieldValue;
    lastUpdated: Date | FieldValue;
    /** Set when this account bills a workspace the user owns. */
    workspaceId?: string;
    workspaceName?: string;
}

export interface BillingAccountUsers {
    billingAccountId: string;
    uid: string;
    dateCreated: Date | FieldValue;
    lastUpdated: Date | FieldValue;
}

// ---------------------------------------------------------------------------
// Team Workspaces (MVP)
// Additive, opt-in tenants. Personal usage (no workspaceId) is unaffected.
// ---------------------------------------------------------------------------

export type WorkspaceType = "team";
export type WorkspaceRole = "owner" | "member" | "contributor";
export type WorkspaceMemberStatus = "active" | "pending" | "revoked";
export type WorkspaceInviteStatus = "pending" | "accepted" | "revoked";

/** Stable machine code for produce/ingress denials (Capture + web). */
export const ROLE_CANNOT_PRODUCE = "role_cannot_produce";
/** Stable machine code when Contributor seats are exhausted. */
export const WORKSPACE_SEAT_REQUIRED = "workspace_seat_required";

export function normalizeWorkspaceRole(role: unknown): WorkspaceRole {
    if (role === "owner") return "owner";
    if (role === "contributor") return "contributor";
    // Legacy stored id "editor" maps to member.
    return "member";
}

export function workspaceRoleCanProduce(role: WorkspaceRole | unknown): boolean {
    const normalized = normalizeWorkspaceRole(role);
    return normalized === "owner" || normalized === "contributor";
}

export interface Workspace {
    id: string;
    name: string;
    type: WorkspaceType;
    storageAnchorUid: string;
    billingAccountId: string;
    primaryOwnerUid?: string;
    favoriteSourceLanguages?: string[];
    dateCreated: Date | FieldValue;
    lastUpdated: Date | FieldValue;
}

export interface WorkspaceMember {
    id: string;
    workspaceId: string;
    uid: string;
    role: WorkspaceRole;
    status: WorkspaceMemberStatus;
    invitedEmail?: string;
    invitedByUid?: string;
    dateCreated: Date | FieldValue;
    lastUpdated: Date | FieldValue;
}

export interface WorkspaceInvite {
    id: string;
    workspaceId: string;
    workspaceName?: string;
    recipientEmailNormalized: string;
    recipientEmail?: string;
    role: WorkspaceRole;
    status: WorkspaceInviteStatus;
    invitedByUid: string;
    invitedByEmail?: string;
    invitedByName?: string;
    invitedAt?: Date | FieldValue;
    lastSentAt?: Date | FieldValue;
    sendCount?: number;
    claimedByUid?: string;
    claimedAt?: Date | FieldValue;
    expiresAt?: Date | FieldValue;
    lastAcceptUrl?: string;
    lastLoginUrl?: string;
    lastSignupUrl?: string;
    lastEmailQueued?: boolean;
    lastEmailAttemptedAt?: Date | FieldValue;
    inviteEmailEventVersion?: number;
    lastUpdated: Date | FieldValue;
}

// Membership summary returned by the listMyWorkspaces callable: the workspace
// plus the caller's role and whether the workspace has an active subscription.
export interface WorkspaceMembership {
    workspace: Workspace;
    role: WorkspaceRole;
    status: WorkspaceMemberStatus;
    subscriptionActive: boolean;
}

export interface BillingTransactions {
    id: string;
    billingAccountId: string;
    value: number;
    description?: string;
    objectId?: string; // object id the transaction relates to
    objectType?: string; // object type the transaction relates
    dateCreated: Date | FieldValue;
    stripeSessionId?: string; // External identifier for the Stripe transaction
    languageCode?: string;
}

export interface BillingTransactionParams {
    billingAccountId: string;
    value: number;
    description?: string;
    objectId?: string; // object id the transaction relates to
    objectType?: string; // object type the transaction relates
    stripeSessionId?: string; // External identifier for the Stripe transaction
    languageCode?: string;
}

export interface Transcription {
    id?: string;
    userVideoVersion: number;
    userVideoId: string;
    creator: string;
    language: string;
    segments: Segment[];
    speakers: Speaker[];
    pipelineUsed: string;
    translated: boolean;
    isSourceTranscript: boolean;
    readOnly: boolean;
    dubbingType?: string;
    captionsOnly?: boolean;
    dateCreated: FieldValue | Date;
    lastUpdated: FieldValue | Date;
}

export interface Segment {
    start: number;
    end: number;
    speaker: string;
    text: string;
    speakerUpdated?: boolean;
    textUpdated?: boolean;
    startUpdated?: boolean;
    endUpdated?: boolean;
    deleted?: boolean;
    deleteAcknowledged?: boolean;
    skipTranslate?: boolean;
}

export interface Speaker {
    id: string;
    label: string;
    voice: string | null;
    voiceDisplayName: string | null;
    voiceCountryCode?: string | null;
    labelUpdated?: boolean;
    voiceUpdated?: boolean;
}

export interface Voice {
    id: string;
    sourceId: string;
    countryCode: string;
    displayName: string;
    gender: string;
    language: string;
    sourceSystem: string;
    sample_url: string;
}

export interface Subscription {
    id: string | null,
    productId?: string | null,
    type: string | null,
    status: string | null,
    priceId: string | null,
    priceInterval: string | null,
    subscriptionPrice: number,
    hostedVideoUsage: number,
    userStripeId: string | null,
    videoLengthLimit: number,
    /** Hosted/managed video minutes included in the plan (from Stripe entitlements). */
    hostedVideoLimit?: number,
    usageBillingEnabled: boolean,
    overageAmount?: number,
    usageBillingSuspendedReason?: "overage_payment_failed" | null,
    pendingOverageInvoiceUrl?: string | null,
    hasStripeCustomer: boolean,
    hasPaymentMethod: boolean,
    hasChargeablePaymentMethod?: boolean,
    paymentMethodType?: string | null,
    hostedInvoiceUrl?: string | null,
    billingRelationship: "free_without_card" | "free_with_card" | "paid_or_trial",
    billingStateSource: "stripe" | "billing_account_cache" | "local_cache",
    billingStateStale: boolean,
    youtubeIntegrationLimit?: number,
    /** Competitor channels the plan may pin. 0 means unlimited. */
    competitorChannelLimit?: number,
    additionalLanguageLimit?: number | null,
    /** Workspace this plan is billed to. Absent for a personal account. */
    licensedWorkspaceId?: string | null,
    licensedWorkspaceName?: string | null,
}

export interface Folder {
    id: string;
    name: string;
    creator: string;
    workspaceId?: string;
    createdByUid?: string;
    parentId?: string | null;
    videos: string[];
    deleted: boolean;
    dateCreated: FieldValue | Date;
    lastUpdated: FieldValue | Date;
}

export type IntegrationType = "webhook" | "youtube" | "instagram" | "linkedin" | "tiktok";

export interface BaseIntegration {
    id: string;
    type: IntegrationType;
    name: string;
    creator: string;
    workspaceId?: string;
    createdByUid?: string;
    deleted: boolean;
    dateCreated: FieldValue | Date;
    lastUpdated: FieldValue | Date;
}

export interface Webhook extends BaseIntegration {
    type: "webhook";
    url: string;
    description?: string;
    contentType: string;
}

export interface YouTubePlaylist {
    id: string;
    title: string;
    description: string;
    privacyStatus: "public" | "private" | "unlisted";
    itemCount: number;
    publishedAt: string;
    etag?: string;
}

export interface YouTubeIntegration extends BaseIntegration {
    // Legacy: used channelId for parent linkage
    parentChannelId?: string | null;
    // New: use integration id for parent linkage to avoid channelId collisions
    parentIntegrationId?: string | null;
    type: "youtube";
    channelId: string;
    channelTitle: string;
    isActive: boolean;
    language: string;
    longUploadsStatus: string;
    syncPlaylists?: boolean;
    playlists?: YouTubePlaylist[];
    playlistsEtag?: string;
    lastPlaylistSync?: FieldValue | Date;
    channelThumbnailUrl: string;
    channelDescription: string;
    channelSubscriberCount: number;
    channelViewCount: number;
    channelVideoCount: number;
    channelCustomUrl: string;
    // Localized language metadata (similar to UserVideo.languages)
    languages?: {
        [languageCode: string]: { title: string; description: string };
    };
}

export interface InstagramIntegration extends BaseIntegration {
    type: "instagram";
    instagramUserId: string;
    username: string;
    profilePictureUrl?: string;
    isActive: boolean;
    requiresReauth: boolean;
}

export interface LinkedInIntegration extends BaseIntegration {
    type: "linkedin";
    /** @deprecated Firestore uses profileId */
    linkedinUserId?: string;
    profileId?: string;
    profileName: string;
    profilePictureUrl?: string;
    /** From LinkedIn API (Firebase stores profileImageUrl) */
    profileImageUrl?: string;
    isActive: boolean;
    requiresReauth: boolean;
    communityConnected?: boolean;
    communityRequiresReauth?: boolean;
    communityTokenInvalidReason?: string | null;
    /**
     * Persisted cache of LinkedIn page/company posting destinations fetched
     * during Pages connection / refresh flow. Stored on Integrations doc so
     * publish UI can avoid live API calls when LinkedIn throttles.
     */
    connectedPages?: Array<{
        urn: string;
        name: string;
        logoUrl?: string;
        role?: string;
    }>;
    /** urn:li:person:… or urn:li:organization:… — where posts are published */
    postingAuthorUrn?: string | null;
}

export interface TikTokIntegration extends BaseIntegration {
    type: "tiktok";
    tiktokOpenId: string;
    profileDisplayName?: string;
    profileAvatarUrl?: string;
    isActive: boolean;
    requiresReauth: boolean;
    creatorInfo?: {
        creatorAvatarUrl?: string;
        commentDisabled?: boolean;
        duetDisabled?: boolean;
        stitchDisabled?: boolean;
        privacyLevelOptions?: string[];
        maxVideoPostDurationSec?: number;
    };
}

export interface UserDocument {
    id: string;
    creator: string;
    filename: string;
    mimeType: string;
    url: string;
    storagePath: string;
    pageCount: number;
    fileSize: number;
    tags: string[];
    dateCreated: FieldValue | Date;
    lastUpdated: FieldValue | Date;
}

export type VoiceMode = "auto" | "design" | "clone";

export interface UserSpeech {
    id: string;
    creator: string;
    workspaceId?: string;
    createdByUid?: string;
    text: string;
    languageCode: string;
    voiceMode: VoiceMode;
    voiceParams?: Record<string, string> | null;
    speed: number;
    durationSeconds: number;
    storagePath: string;
    dateCreated: FieldValue | Date;
    deleted: boolean;
}

export type VoiceCloneGender = "male" | "female" | "other";
export type VoiceCloneAge = "child" | "teenager" | "young adult" | "middle-aged" | "elderly";
export type VoiceCloneStatus = "processing" | "ready" | "failed" | "rejected";

export type VoiceCloneApproval = "pending" | "approved" | "rejected";

export interface VoiceClone {
    id: string;
    creator: string;
    workspaceId?: string;
    createdByUid?: string;
    name: string;
    description: string;
    language: string;
    gender: VoiceCloneGender;
    age: VoiceCloneAge;
    sampleAudioPath: string;
    sampleAudioUrl: string;
    enhancedSampleAudioPath?: string;
    enhancedSampleAudioUrl?: string;
    promptTensorPath: string;
    refText: string;
    enhancementApplied?: boolean;
    enhancementMode?: string;
    enhancementAudioForClone?: string;
    refTextSource?: string;
    detectedLanguage?: string;
    detectedLanguageProbability?: number;
    enhancementError?: string;
    status: VoiceCloneStatus;
    approval: VoiceCloneApproval;
    moderatedBy?: string;
    moderatedAt?: FieldValue | Date | null;
    dateCreated: FieldValue | Date;
    deleted: boolean;
}

export interface DocsTranslateRequest {
    id: string;
    creator: string;
    userDocumentId: string;
    sourceLanguage: string;
    targetLanguage: string;
    status: "pending" | "processing" | "complete" | "failed" | "retrying";
    errorMessage?: string;
    translatedDocsUrl?: string;
    translatedDocsStoragePath?: string;
    dateCreated: FieldValue | Date;
    lastUpdated: FieldValue | Date;
}

export interface VideoExport {
    id: string;
    channelId: string;
    creator: string | null;
    dateCreated: FieldValue | Date;
    externalVideoId: string;
    externalVideoUrl: string;
    integrationId: string | null;
    integrationType: "youtube" | "webhook" | "linkedin" | "instagram" | "tiktok";
    languageCode: string;
    lastUpdated: FieldValue | Date;
    metadata: VideoMetadata;
    description: string;
    title: string;
    privacyStatus: YoutubePrivacy;
    status: string;
    userVideoId: string;
    shortOutputId?: string;
    userShortsId?: string;
    scheduledPublishAt?: string;
    // Track published localizations to YouTube
    publishedLocalizations?: {
        [languageCode: string]: {
            title: string;
            description: string;
            publishedAt: Date;
        };
    };
    // Track published captions to YouTube
    publishedCaptions?: {
        [languageCode: string]: {
            captionId: string;
            publishedAt: Date;
            transcriptionLastUpdated: Date | null;
        };
    };
    // Track thumbnail updates
    thumbnailUpdatedAt?: Date;
}

export interface ApiKey {
    id: string;
    name: string;
    creator: string;
    apiKey: string;
    deleted: boolean;
    lastUsed?: string;
    dateCreated: string;
    dateUpdated: string;
    requestsInProgress: number;
}

// UserShorts types
export type ShortsStatus = "pending" | "processing" | "completed" | "failed";
// "longclip" is the internal/back-end label for the UI-renamed "Clips"
// (5–25 min 16:9 subject arcs). The existing "clip" stays as-is for the
// UI-renamed "Shorts". Order: short-form first, long-form last.
export type ShortOutputType = "clip" | "highlight" | "promo" | "longclip" | "captions";
export type ShortsAspectRatio = "9:16" | "16:9" | "1:1";

export interface ShortsConfig {
    aspectRatio: ShortsAspectRatio;
    captionStyleId: string;
}

export interface ClipConfig {
    maxDuration: number;
    minDuration: number;
    numClips: number;
    outputType: ShortOutputType;
}

/**
 * Config shape for the "Clips" (long-form) generation mode. The duration
 * bounds are fixed server-side (5–25 min) and intentionally NOT exposed
 * to the user — only the number of clips (and caption styling) is
 * user-configurable. No soundtrack option (unlike promos). Aspect ratio
 * is always 16:9. No watermark option — long outputs are not re-encoded
 * for a burn-in (product decision).
 *
 * Mirrors `LongClipConfig` in braiv-shorts and `LONGCLIP_DEFAULTS` in
 * braiv-firebase `shorts/ShortsRequest.ts`.
 */
export interface LongClipConfig {
    /** Number of long clips to generate (1..4). */
    numClips: number;
    captionStyleId: string;
    captionStyle?: string | null;
    captionPosition?: "top" | "middle" | "bottom";
    captionTextColor?: string;
    captionHighlightColor?: string;
    /** Optional steering prompt for the LLM subject-arc selector. */
    clipSelectionPrompt?: string;
}

/** UI-only hard bounds; kept in sync with `LONGCLIP_*` in braiv-firebase. */
export const LONG_CLIP_MIN_NUM = 1
export const LONG_CLIP_MAX_NUM = 4
export const LONG_CLIP_MIN_DURATION_SEC = 5 * 60
export const LONG_CLIP_MAX_DURATION_SEC = 25 * 60
/** Long-clip requests require at least this much source video. Must match
 * the `failed-precondition` threshold in createLongClipRequest. */
export const LONG_CLIP_MIN_SOURCE_DURATION_SEC = LONG_CLIP_MIN_DURATION_SEC * 2

/** Minimum source video duration (seconds) for AI Shorts in Braiv Studio and the shorts-only import wizard. */
export const MIN_SHORTS_SOURCE_DURATION_SECONDS = 3 * 60

/** Captions Only is offered for source videos shorter than this. */
export const CAPTIONS_ONLY_MAX_DURATION_SECONDS = 10 * 60

/** At or under this duration, Captions Only is the only shorts mode. */
export const CAPTIONS_ONLY_EXCLUSIVE_MAX_DURATION_SECONDS = 5 * 60

export interface ShortSourceTimestamps {
    startTime: number;
    endTime: number;
}

export interface ShortOutputPublishDestination {
    platform?: "youtube" | "instagram" | "linkedin" | "webhook" | string;
    status?: "published" | "scheduled" | "publishing" | "pending" | "failed" | string;
    externalVideoId?: string;
    externalVideoUrl?: string;
    scheduledPublishAt?: string;
    publishedAt?: string;
    channelId?: string;
    videoExportId?: string;
    error?: string;
    errorCode?: string;
    authRequired?: boolean;
    youtubeVideoId?: string;
    youtubeVideoUrl?: string;
    youtubeChannelId?: string;
    youtubePlaylistId?: string;
    youtubePlaylistItemId?: string;
    instagramMediaId?: string;
    instagramPermalink?: string;
    instagramContainerId?: string;
    linkedinPostId?: string;
    linkedinPostUrl?: string;
    linkedinVideoUrn?: string;
    tiktokPublishId?: string;
    tiktokPublicPostId?: string;
    tiktokPostUrl?: string;
}

export type ShortCaptionEditStatus =
    | "idle"
    | "queued"
    | "pending"
    | "rendering"
    | "rendered"
    | "failed";

export interface ShortOutput {
    id: string;
    title: string;
    description?: string;
    /** Burned-in top badge. Absent when the short was generated without a hook. */
    hook?: string;
    type: ShortOutputType;
    aspectRatio: ShortsAspectRatio;
    duration: number;
    sourceTimestamps: ShortSourceTimestamps;
    videoUrl: string;
    videoStatus?: "processing" | "complete" | "failed" | string;
    videoGcsPath: string;
    subtitlesUrl: string;
    subtitlesGcsPath: string;
    transcriptionId: string;
    cropPlanId?: string;
    thumbnailUrl?: string;
    thumbnailStatus?: "pending" | "complete" | "failed" | string;
    thumbnailPrompt?: string;
    generatedThumbnailUrl?: string;
    generatedThumbnailRequestId?: string;
    deleted?: boolean;
    deletedAt?: string;
    deletedBy?: string;
    publishes?: Record<string, ShortOutputPublishDestination>;

    // Wave 1.1 master-transcript round-trip — array of indices into the
    // source Transcriptions/{id}.segments array that this clip spans.
    // Missing on shorts generated before the round-trip was introduced;
    // the caption editor's Edit button is gated on its presence.
    masterSegmentIndexes?: number[];

    // Wave 8 E.3 — pre-captions resized intermediate. When present, the
    // rerender-short-captions worker can re-burn captions in ~5–10 s
    // instead of running the full trim + resize + align pipeline.
    clipResizedNoCaptionsUrl?: string;
    clipResizedNoCaptionsGcsPath?: string;

    // Wave 8 E.4 — caption style snapshot recorded at render time so the
    // rerender worker can reproduce the exact original look without
    // depending on ShortsRequests still being present/unchanged.
    captionStyleId?: string;
    captionPosition?: string;
    captionTextColor?: string;
    captionHighlightColor?: string;
    includeWatermark?: boolean;
    /**
     * True when generation forced the Braiv watermark because the account
     * was free and On Demand was off. False when the account could opt out.
     * Missing on shorts generated before this flag existed.
     */
    braivWatermarkForced?: boolean;
    /** User logo burned in place of the Braiv watermark. */
    customLogoUrl?: string;
    /** True when the Braiv or custom logo was burned into the delivered MP4. */
    hasWatermark?: boolean;
    watermarkRemovalStatus?: "idle" | "queued" | "pending" | "rendering" | "rendered" | "failed";
    watermarkRemovalQueuedAt?: FieldValue | Date;
    lastWatermarkRemovalAt?: FieldValue | Date;
    lastWatermarkRemovalError?: string;

    // Wave 8 — caption editor lifecycle fields. The Firestore trigger
    // sets `queued` on fan-out, the worker flips to `rendering` on start
    // and `rendered`/`failed` on completion.
    captionEditStatus?: ShortCaptionEditStatus;
    captionEditQueuedAt?: FieldValue | Date;
    lastCaptionEditAt?: FieldValue | Date;
    lastCaptionEditError?: string;

    // Crop editor lifecycle fields. The full scene/keyframe data lives in
    // ShortsCropPlans/{cropPlanId}; this output keeps only the small pointer.
    cropEditStatus?: "idle" | "queued" | "rendering" | "rendered" | "failed";
    cropEditQueuedAt?: FieldValue | Date;
    lastCropEditAt?: FieldValue | Date;
    lastCropEditError?: string;

    // Monotonically-increasing token bumped on every successful
    // rerender so the UI can force a fresh video fetch via `?v=<token>`
    // and invalidate the browser's MP4 cache.
    videoCacheBustToken?: number;
}

export interface UserShorts {
    id: string;
    userVideoId: string;
    creator: string;
    language: string;
    status: ShortsStatus;
    taskId: string;
    sourceType: string;
    sourceUrl: string | null;
    sourceGcsPath: string | null;
    config: ShortsConfig;
    clipConfig: ClipConfig;
    outputs: ShortOutput[];
    thumbnailUrl?: string;
    dateCreated: FieldValue | Date;
    lastUpdated: FieldValue | Date;
}

export interface ShortsPublishQueueItem {
    id: string;
    outputId: string;
    userShortsId: string;
    integrationId: string;
    status: "pending" | "scheduled" | "published" | "failed";
    scheduledPublishAt?: Date;
    userId?: string;
    scheduledBy?: string;
    createdAt?: Date;
    lastUpdated?: Date;
}

export type VideoPublishQueueStatus = "pending" | "starting" | "started" | "failed" | "cancelled"

export interface VideoPublishQueueItem {
    id: string
    batchId: string
    creator: string
    workspaceId?: string
    scheduledBy: string
    userVideoId: string
    integrationId: string
    channelId?: string
    languageCode: string
    orderIndex: number
    scheduledStartAt: Date
    mode: "immediate" | "interval"
    status: VideoPublishQueueStatus
    exportRequestId?: string
    errorMessage?: string
    errorCode?: string
    scheduleTimeZone?: string
    scheduleLocalTime?: string
}

export type PublishScheduleEntry = {
    source: "video-queue" | "shorts-queue" | "youtube-native"
    id: string
    ownerUid: string
    kind: "long-form" | "short"
    userVideoId?: string
    userShortsId?: string
    outputId?: string
    title: string
    thumbnailUrl?: string
    platform: string
    integrationId: string
    channelTitle?: string
    languageCode?: string
    scheduledFor: string
    scheduleTimeZone?: string
    status: "pending" | "in-progress" | "done" | "failed" | "cancelled"
    batchId?: string
}

